"use client";

import { useSearchParams } from "next/navigation";
import { useCallback, useEffect } from "react";
import { useAPI } from "apps/website/hooks/useAPI";
import { useDatadog } from "@auth/client-sdk-react";
import debounce from "lodash.debounce";

import { useSignupFlowStore } from "./useSignUpFlowStore";

export const useSignUpFlowQueryParams = () => {
  const queryParams = useSearchParams();
  const api = useAPI();
  const datadog = useDatadog();
  const {
    subscription,
    updateSubscription,
    callGetAvailableFreshPlansAndUpdateStore,
    isStoreInitialised,
  } = useSignupFlowStore((state) => ({
    subscription: state.subscription,
    updateSubscription: state.updateSubscription,
    callGetAvailableFreshPlansAndUpdateStore: state.callGetAvailableFreshPlansAndUpdateStore,
    isStoreInitialised: state.isStoreInitialised,
  }));

  const onAttemptToApplyDiscountCode = async (discountCode: string) => {
    const shouldRefetchSubscriptionPlans: boolean = !!subscription?.trial?.totalOriginalPrice;
    try {
      const { data } = await api.Discounts.validateDiscountCode(discountCode);
      const newDiscountCode = data.discount.code;
      if (newDiscountCode) {
        updateSubscription({ discount: { discountCode: newDiscountCode } });
      }
      if (newDiscountCode && shouldRefetchSubscriptionPlans) {
        await callGetAvailableFreshPlansAndUpdateStore({ discountCode: newDiscountCode });
      }

    } catch (error) {
      datadog.logger.error("Couldn't apply discount code from query parameter", { error });
    }
  };

  const queryParamMethodMap: Record<string, (value: string) => void | Promise<void>> = {
    discount_code: onAttemptToApplyDiscountCode,
  };

  useEffect(() => {
    if (isStoreInitialised && !!queryParams.toString()) {
      onHandleQueryParamsUpdate();
    }
  }, [ queryParams.toString(), isStoreInitialised ]);

  // Debounce to prevent multiple calls - Should probably implement in in-flight abortion
  // for these endpoints in the future
  const onHandleQueryParamsUpdate = useCallback(debounce(() => {
    Object.entries(queryParamMethodMap).forEach(([ key, handler ]) => {
      const queryValue = queryParams.get(key);
      if (queryValue) {
        void handler(queryValue);
      }
    });
  }, 100), [ queryParams ]);

};
